import { useState } from 'react';
import { Lock } from 'lucide-react';
import { toast } from 'react-toastify';
import { AuthLayout } from '../../../components/layouts';
import { Stepper } from '../../../components/ui';
import AuthInput from '../../../components/ui/AuthInput';
import AuthPrimaryButton from '../../../components/ui/AuthPrimaryButton';
import { AuthProps } from './PersonalInfo';

function passwordChecks(password: string) {
    return {
        length: password.length >= 8,
        upper: /[A-Z]/.test(password),
        number: /[0-9]/.test(password),
        special: /[^A-Za-z0-9]/.test(password),
    };
}

/** Step 2 — Password (original flow) with new design. */
export default function PasswordInfo({ props, setActiveTab }: AuthProps) {
    const { setFormData, formData } = props;
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(false);

    const checks = passwordChecks(formData.password || '');
    const allChecks = checks.length && checks.upper && checks.number && checks.special;

    const respObj = localStorage.getItem('email-confirmation');
    const obj = respObj ? JSON.parse(respObj) : null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (formData.password !== confirmPassword) {
            setError(true);
            toast.error('Passwords must match!');
            return;
        }
        if (!allChecks) {
            toast.error('Password does not meet the requirements');
            return;
        }
        setError(false);
        if (obj?.message) toast.success(obj.message);
        setActiveTab();
    };

    const CheckItem = ({ ok, label }: { ok: boolean; label: string }) => (
        <li className={`flex items-center gap-2 text-[12px] ${ok ? 'text-emerald-600' : 'text-[#8b9aab]'}`}>
            <span
                className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold ${
                    ok ? 'bg-emerald-100 text-emerald-700' : 'bg-[#EDE7F0] text-[#94a3b8]'
                }`}
            >
                {ok ? '✓' : ''}
            </span>
            {label}
        </li>
    );

    return (
        <AuthLayout>
            <Stepper currentTab={2} />

            <div className="mb-5 text-center">
                <h1 className="font-[AvertaStd-Semibold] text-[22px] text-ryd-headerTextPrimary sm:text-[26px]">
                    Create a password
                </h1>
                <p className="mt-1.5 px-1 text-[13px] text-[#8b9aab] sm:text-[14px]">
                    Choose a strong password to secure your account.
                </p>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="grid gap-4 sm:grid-cols-2">
                    <AuthInput
                        label="Password"
                        isPassword
                        placeholder="Enter password"
                        required
                        leftIcon={<Lock size={18} />}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    />
                    <AuthInput
                        label="Confirm Password"
                        isPassword
                        placeholder="Confirm password"
                        required
                        leftIcon={<Lock size={18} />}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                </div>

                {error ? (
                    <p className="text-[12px] font-medium text-red-600">Passwords must match!</p>
                ) : null}

                <div className="rounded-[14px] border border-ryd-primary/15 bg-[#F8F0F6] px-4 py-3">
                    <p className="mb-2 text-[12px] font-semibold text-ryd-primary">Password must contain:</p>
                    <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                        <CheckItem ok={checks.length} label="At least 8 characters" />
                        <CheckItem ok={checks.upper} label="One uppercase letter" />
                        <CheckItem ok={checks.number} label="One number" />
                        <CheckItem ok={checks.special} label="One special character" />
                    </ul>
                </div>

                <AuthPrimaryButton>Continue</AuthPrimaryButton>
            </form>
        </AuthLayout>
    );
}
